import React, { useEffect, useRef, useState } from 'react';

type RevealVariant = 'fade-up' | 'fade-right' | 'fade-left' | 'blur-in' | 'zoom-in' | 'elastic-up';

interface RevealProps {
  children: React.ReactNode;
  variant?: RevealVariant;
  delay?: number; // in ms
  className?: string;
  duration?: number; // in seconds
}

export const Reveal: React.FC<RevealProps> = ({ 
  children, 
  variant = 'fade-up', 
  delay = 0, 
  className = '',
  duration = 0.8
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '0px 0px -50px 0px' // Offset slightly so it triggers before bottom
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const getAnimationClass = () => {
    switch (variant) {
      case 'fade-right': return 'animate-reveal-right';
      case 'fade-left': return 'animate-reveal-left';
      case 'blur-in': return 'animate-reveal-blur';
      case 'zoom-in': return 'animate-reveal-zoom';
      case 'elastic-up': return 'animate-reveal-elastic';
      default: return 'animate-reveal-up';
    }
  };

  return (
    <div
      ref={ref}
      // Using 'invisible' initially instead of 'opacity-0' to avoid potential conflicts, 
      // but relying on animation-fill-mode: both handles the visibility during delay.
      // We keep opacity-0 for the initial render state before intersection.
      className={`${className} ${isVisible ? getAnimationClass() : 'opacity-0'}`}
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: `${duration}s`,
        animationFillMode: 'both', // Changed from 'forwards' to 'both' to fix delay flash
        willChange: 'transform, opacity, filter' // Optimization
      }}
    >
      {children}
    </div>
  );
};